import React, { useEffect, useState } from "react";
import api from "../../api";
import { Loader2, Send, ClipboardList } from "lucide-react";
import { toast } from "../../hooks/use-toast";

export default function WaitlistTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [mode, setMode] = useState("off");

  const load = () => api.get("/business/waitlist").then(({ data }) => setData(data));
  useEffect(() => {
    load();
    api.get("/business/me").then(({ data }) => setMode(data.waitlist_release_mode || "off")).catch(() => {});
  }, []);

  const saveMode = async (m) => {
    setMode(m);
    try {
      await api.put("/business/me", { waitlist_release_mode: m });
      toast({ title: "Saved", description: m === "off" ? "Auto-release off — you'll notify manually." : m === "next" ? "Freed seats will be offered to the next person in line." : "Freed seats will alert everyone waiting (first come, first served)." });
    } catch {
      toast({ title: "Error", description: "Could not save setting", variant: "destructive" });
    }
  };

  const notify = async (g) => {
    setBusy(`${g.product_id}-${g.tier_id}`);
    try {
      const { data } = await api.post("/business/waitlist/notify", { product_id: g.product_id, tier_id: g.tier_id });
      if (!data.notified) {
        toast({ title: "Nobody waiting", description: data.reason || "No one to notify." });
      } else if (data.email_configured) {
        toast({ title: "Waitlist notified", description: `Emailed ${data.emailed} of ${data.notified} waiting fans.` });
      } else {
        toast({ title: "Marked as notified", description: `${data.notified} entries marked. Configure SMTP in admin to actually send emails.`, variant: "destructive" });
      }
      load();
    } catch {
      toast({ title: "Error", description: "Could not notify waitlist", variant: "destructive" });
    } finally { setBusy(""); }
  };

  if (!data) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;

  return (
    <div data-testid="waitlist-tab">
      <div className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)] mb-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="waitlist-release-setting">
        <div>
          <p className="font-semibold text-[#16243b]">Auto-release freed seats</p>
          <p className="text-sm text-[#5a6b80]">When a ticket is cancelled and a seat opens up, who should we tell?</p>
        </div>
        <select value={mode} onChange={(e) => saveMode(e.target.value)} data-testid="release-mode-select"
          className="rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] bg-white">
          <option value="off">Off — I'll notify manually</option>
          <option value="next">Offer to next in line</option>
          <option value="all">Notify everyone (first come, first served)</option>
        </select>
      </div>
      {data.groups.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center text-[#8a97a8] shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
          <ClipboardList className="mx-auto mb-3 text-[#c3ccd8]" size={40} />
          No one on a waitlist yet. When a ticket tier sells out, fans can join the waitlist from your event page.
        </div>
      ) : (
        <div className="space-y-4">
          {data.groups.map((g) => (
            <div key={`${g.product_id}-${g.tier_id}`} className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)] flex items-center justify-between" data-testid={`waitlist-group-${g.tier_id}`}>
              <div>
                <p className="font-semibold text-[#16243b]">{g.product_title}</p>
                <p className="text-sm text-[#5a6b80]">{g.tier_name}</p>
                <p className="text-xs text-[#8a97a8] mt-1">
                  <span className="font-semibold text-[#2ec9c9]">{g.waiting}</span> waiting · {g.notified} already notified
                </p>
              </div>
              <button onClick={() => notify(g)} disabled={busy === `${g.product_id}-${g.tier_id}` || g.waiting === 0}
                data-testid={`notify-waitlist-${g.tier_id}`}
                className="gp-btn-teal rounded-xl px-5 py-2.5 text-sm font-medium flex items-center gap-2 disabled:opacity-50">
                {busy === `${g.product_id}-${g.tier_id}` ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                Notify waitlist
              </button>
            </div>
          ))}
        </div>
      )}

      {data.entries.length > 0 && (
        <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-hidden mt-6">
          <table className="w-full text-sm">
            <thead className="bg-[#f6f8fb] text-[#8a97a8]"><tr>{["Name", "Email", "Event", "Tier", "Status"].map((h) => <th key={h} className="text-left px-5 py-3 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {data.entries.map((e) => (
                <tr key={e.id} className="border-t border-gray-50">
                  <td className="px-5 py-3 text-[#16243b]">{e.name || "—"}</td>
                  <td className="px-5 py-3 text-[#5a6b80]">{e.email}</td>
                  <td className="px-5 py-3 text-[#5a6b80]">{e.product_title}</td>
                  <td className="px-5 py-3 text-[#5a6b80]">{e.tier_name}</td>
                  <td className="px-5 py-3">{e.notified
                    ? <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700">Notified</span>
                    : <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">Waiting</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
